import { LoginService } from "./../../login/login.service";
import { Injectable } from "@angular/core";
import { Observable, Subject, firstValueFrom, lastValueFrom } from "rxjs";
import { LocalStorageService } from "src/app/utils/localstorage.service";
import { environment } from "../../../environments/environment";
import { HttpClient, HttpHeaders } from "@angular/common/http";

@Injectable({ providedIn: "root" })
export class EmailService {
  resetEmailState$ = new Subject();
  newEmailSent$ = new Subject();

  settings: any = {
    signature: "",
    greeting: "",
  };

  public constructor(
    private localStorage: LocalStorageService,
    public _http: HttpClient,
    public loginService: LoginService,
  ) {}

  getEmails(
    inboxUuid,
    mailbox = "PRIORITY",
    start = 0,
    rows = 20,
  ): Observable<any> {
    return this._http.get(
      `${environment.apiUrl}/email/${inboxUuid}/mailbox/${mailbox}?start=${start}&rows=${rows}`,
    );
  }

  getAuthStatus(): Observable<any> {
    return this._http.get(`${environment.apiUrl}/email/auth/status`);
  }

  getInboxState(account, inboxUuid): Observable<any> {
    return this._http.post(
      `${environment.apiUrl}/email/inbox/${inboxUuid}/state`,
      {
        account,
      },
    );
  }

  getInboxSettings(inboxUuid): Observable<any> {
    return this._http.get(`${environment.apiUrl}/email/inbox/${inboxUuid}`);
  }

  saveInboxSettings(inboxUuid, settings): Observable<any> {
    return this._http.patch(
      `${environment.apiUrl}/email/inbox/${inboxUuid}`,
      settings,
    );
  }

  getInboxList(): Observable<any> {
    return this._http.get(`${environment.apiUrl}/email/inbox/list`);
  }

  getAuthUrl(provider, account): Observable<any> {
    return this._http.post(`${environment.apiUrl}/email/auth/url/${provider}`, {
      account,
    });
  }

  verifyLoginInformation(account, password): Observable<any> {
    return this._http.post(`${environment.apiUrl}/email/auth/verify`, {
      account,
      password,
    });
  }

  createAccount(account, password): Observable<any> {
    return this._http.post(`${environment.apiUrl}/email/auth/create`, {
      account,
      password,
    });
  }

  syncEmail(): Observable<any> {
    return this._http.get(`${environment.apiUrl}/email/sync`);
  }

  getEmailThread(uuid): Observable<any> {
    return this._http.get(`${environment.apiUrl}/email/thread/${uuid}`);
  }

  deleteEmailThread(uuid): Observable<any> {
    return this._http.delete(`${environment.apiUrl}/email/thread/${uuid}`);
  }

  archiveEmailThread(uuid): Observable<any> {
    return this._http.delete(
      `${environment.apiUrl}/email/thread/${uuid}/archive`,
    );
  }

  readEmailThread(uuid): Promise<any> {
    return this._http
      .post(`${environment.apiUrl}/email/thread/${uuid}/read`, {})
      .toPromise();
  }

  getThreadInfo(uuid): Promise<any> {
    return this._http
      .get(`${environment.apiUrl}/email/thread/${uuid}/info`)
      .toPromise();
  }

  sendEmail(to, subject, message, cc, bcc, emailId = null): Observable<any> {
    return this._http.post(`${environment.apiUrl}/email/send`, {
      to: to,
      subject: subject,
      message: message,
      emailId: emailId,
      cc: cc,
      bcc: bcc,
    });
  }

  changeEmailTone(email, tone, threadUuid): Promise<any> {
    return lastValueFrom(
      this._http.post(`${environment.apiUrl}/email/tone`, {
        email: email,
        tone: tone,
        threadUuid: threadUuid,
      }),
    );
  }

  summarizeThread(uuid): Observable<any> {
    return this._http.post(
      `${environment.apiUrl}/email/thread/${uuid}/summarize`,
      {},
    );
  }

  magicEmail(email, threadUuid): Observable<any> {
    return this._http.post(`${environment.apiUrl}/email/magic`, {
      email: email,
      threadUuid: threadUuid,
    });
  }

  getUnreadThreadsCount(): Observable<any> {
    return this._http.get(`${environment.apiUrl}/email/threads/unread/count`);
  }

  createJob(threadUuid, clientCompanyId): Observable<any> {
    return this._http.post(`${environment.apiUrl}/email/threads/job`, {
      threadUuid,
      client_company_id: clientCompanyId,
    });
  }

  removeEmailConnection(inboxUuid): Observable<any> {
    return this._http.delete(`${environment.apiUrl}/email/inbox/${inboxUuid}`);
  }

  getThreadWorkspaces(threadUuid): Promise<any> {
    return this._http
      .get(`${environment.apiUrl}/email/thread/${threadUuid}/workspaces`)
      .toPromise();
  }

  updateThreadWorkspaces(threadUuid, workspaces): Promise<any> {
    return this._http
      .patch(`${environment.apiUrl}/email/thread/${threadUuid}/workspaces`, {
        workspaces,
      })
      .toPromise();
  }

  linkThreadToWorkspace(threadUuid, workspaceUuid): Promise<any> {
    return this._http
      .post(`${environment.apiUrl}/email/thread/${threadUuid}/link`, {
        workspaceUuid: workspaceUuid,
      })
      .toPromise();
  }

  getAttachmentContent(inboxUuid, attachmentId): Observable<any> {
    return this._http.get(
      `${environment.apiUrl}/email/inbox/${inboxUuid}/attachment/${attachmentId}/content`,
      { responseType: "blob" },
    );
  }

  createAttachmentFile(
    inboxUuid,
    attachmentId,
    companyUuid,
    messageUuid,
    attachmentData,
  ) {
    return this._http
      .post(
        `${environment.apiUrl}/email/inbox/${inboxUuid}/attachment/${attachmentId}/file`,
        {
          company_uuid: companyUuid,
          message_uuid: messageUuid,
          attachment_data: attachmentData,
        },
      )
      .toPromise();
  }

  retryConnection(): Promise<any> {
    return firstValueFrom(
      this._http.post(`${environment.apiUrl}/email/retry`, {}),
    );
  }
}
